// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// The agent personas + tier gating for the shared agent surface (WS4 §2). One
// registry, used by WS4 (Merlin, Athena) and WS5 (Socrates, Seldon) alike -- the
// surface is shared, so the persona metadata and the gating predicate live in one
// place. Gating goes through the core tier system's `tierAtLeast`, never a raw
// string compare (F1 / H7).
//
// The persona NAMES, ROLES, and AVATAR ART are Coherence-supplied finished copy
// (the WS4 polish split); the values here are build-time placeholders (MVP avatar
// = a text/initial mark) so behavior and gating can be wired and tested now, then
// swapped for the finished persona spec before the acceptance re-pass.

import { Tier, tierAtLeast } from '@simlin/core/tier';

export type AgentPersona = 'merlin' | 'athena' | 'socrates' | 'seldon';

/**
 * What a persona DOES when the user sends to it -- routes the send to the right
 * agent flow. WS4 builds `build` (Merlin) and `describe` (Athena); `coach`
 * (Socrates) and `validate` (Seldon) are WS5.
 */
export type PersonaAction = 'build' | 'describe' | 'coach' | 'validate';

export interface PersonaMeta {
  readonly id: AgentPersona;
  /** Display name -- placeholder; finished copy is Coherence-supplied. */
  readonly name: string;
  /** One-word role shown under the name. */
  readonly role: string;
  /** MVP avatar: a text/initial mark (agent-library art is a later follow-up). */
  readonly mark: string;
  /** Gating floor: the least-privileged tier that can reach this persona (F1). */
  readonly minTier: Tier;
  /** The flow a send to this persona routes to. */
  readonly action: PersonaAction;
}

// WS4 builds Merlin + Athena; WS5 adds Socrates + Seldon on this same surface.
// Seldon's validation-gate BEHAVIOR is held (pending scope) -- only its
// persona-surface metadata is defined here, not its gates.
export const PERSONAS: Readonly<Record<AgentPersona, PersonaMeta>> = {
  merlin: { id: 'merlin', name: 'Merlin', role: 'Builder', mark: 'M', minTier: Tier.Student, action: 'build' },
  athena: { id: 'athena', name: 'Athena', role: 'Scribe', mark: 'A', minTier: Tier.Member, action: 'describe' },
  socrates: { id: 'socrates', name: 'Socrates', role: 'Initiator', mark: 'S', minTier: Tier.Public, action: 'coach' },
  seldon: { id: 'seldon', name: 'Seldon', role: 'Validator', mark: 'D', minTier: Tier.Member, action: 'validate' },
};

/** Personas built in WS4, in persona-strip order. */
export const WS4_PERSONAS: readonly AgentPersona[] = ['merlin', 'athena'];

/** Whether `tier` can reach `persona` at all (the gate; the upgrade path is UI). */
export function isPersonaAvailable(persona: AgentPersona, tier: Tier): boolean {
  return tierAtLeast(tier, PERSONAS[persona].minTier);
}

/**
 * Filter a persona list to those available at `tier`, preserving order. Used to
 * build the persona strip so a user never sees a chip they can't use; the block
 * + upgrade path (H7) applies when an action is attempted above the tier.
 */
export function availablePersonas(personas: readonly AgentPersona[], tier: Tier): AgentPersona[] {
  return personas.filter((p) => isPersonaAvailable(p, tier));
}

/**
 * Whether the tier may APPLY Merlin's edit in place -- a direct edit with no
 * preview step. Advanced (Manager/Leader) only; Student/Member always go through
 * propose -> preview -> accept. The in-place edit is still undoable in one step,
 * and Member and below can never reach the in-place path, so the Member/Advanced
 * boundary holds by construction (B1b / DC5).
 */
export function canApplyInPlace(tier: Tier): boolean {
  return tierAtLeast(tier, Tier.Advanced);
}
